import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Empty, Input, Segmented, Select, Skeleton, Slider, Switch, message } from "antd";
import {
  StarFilled,
  UserOutlined,
  SoundOutlined,
  CompassOutlined,
  EyeOutlined,
  CustomerServiceOutlined,
  PlayCircleOutlined,
} from "@ant-design/icons";
import { AuthContext } from "../../context/auth-context/AuthContext";
import apiService from "../../services/apiServices";
import { urlPublica } from "../inicio/MisExperiencias";
import { VistaPreviaAsistente } from "./VistaPreviaAsistente";
import { UsoYCostos } from "./UsoYCostos";

const CAPACIDADES = [
  { icono: <CompassOutlined />, titulo: "Llevar a otra escena", texto: "Cuando el visitante lo pide, lo lleva a la escena que corresponde." },
  { icono: <EyeOutlined />, titulo: "Saber qué mira el visitante", texto: "Conoce la escena actual y hacia dónde apunta la cámara." },
  { icono: <CustomerServiceOutlined />, titulo: "Manejar la música", texto: "Activa o silencia la música ambiente de la experiencia." },
];

//mismo rango que el componente del visitante para el suavizado del fondo verde
const SUAV_MIN = 0.015;
const SUAV_MAX = 0.3;
const solidezDesde = (suavizado) =>
  Math.round(((SUAV_MAX - (suavizado ?? 0.05)) / (SUAV_MAX - SUAV_MIN)) * 100);
const suavizadoDesde = (solidez) => +(SUAV_MAX - (solidez / 100) * (SUAV_MAX - SUAV_MIN)).toFixed(4);

//todo lo que se edita en esta pagina, leido de metadata.asistente con los mismos defaults del componente
const formDesde = (a = {}) => {
  const ui = a.ui || {};
  const ub = a.ubicacion || {};
  const rc = a.recorte || {};
  return {
    habilitado: Boolean(a.habilitado),
    agenteId: a.agenteId || "",
    avatarId: a.avatarId || "",
    nombre: ui.nombre || "",
    saludo: ui.saludo || "",
    invitacionTras: ui.invitacionTras ?? 6,
    sugerencias: Array.isArray(ui.sugerencias) ? ui.sugerencias : [],
    subtitulos: ui.subtitulos !== false,
    permitirTexto: ui.permitirTexto !== false,
    ocultarEntreTurnos: Boolean(ui.ocultarEntreTurnos),
    contacto: ui.contacto || "",
    anclaje: ub.anclaje || "abajo-derecha",
    alturaVh: ub.alturaVh || 64,
    margenX: ub.margenX ?? 24,
    margenY: ub.margenY ?? 0,
    modoRecorte: rc.modo || "chroma",
    solidez: solidezDesde(rc.chroma?.suavizado),
    zoom: rc.encuadre?.zoom ?? 1,
    vertical: rc.encuadre?.vertical ?? 0,
  };
};

//metadata.asistente con lo editado encima de lo guardado; lo usan el guardado y la vista previa
const armarAsistente = (previo, form) => ({
  ...previo,
  habilitado: form.habilitado,
  agenteId: form.agenteId.trim(),
  avatarId: form.avatarId.trim(),
  ui: {
    ...(previo.ui || {}),
    nombre: form.nombre.trim(),
    saludo: form.saludo.trim(),
    invitacionTras: form.invitacionTras,
    sugerencias: form.sugerencias.map((t) => t.trim()).filter(Boolean),
    subtitulos: form.subtitulos,
    permitirTexto: form.permitirTexto,
    ocultarEntreTurnos: form.ocultarEntreTurnos,
    contacto: form.contacto.trim(),
  },
  ubicacion: {
    ...(previo.ubicacion || {}),
    anclaje: form.anclaje,
    alturaVh: form.alturaVh,
    margenX: form.margenX,
    margenY: form.margenY,
  },
  recorte: {
    ...(previo.recorte || {}),
    modo: form.modoRecorte,
    chroma: { ...(previo.recorte?.chroma || {}), suavizado: suavizadoDesde(form.solidez) },
    encuadre: { zoom: form.zoom, vertical: form.vertical },
  },
});

const Campo = ({ etiqueta, children, ayuda }) => (
  <label style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 13, color: "var(--inm-texto-2)" }}>
    {etiqueta}
    {children}
    {ayuda && <span className="inm-tenue" style={{ fontSize: 12 }}>{ayuda}</span>}
  </label>
);

const Interruptor = ({ checked, onChange, children }) => (
  <label style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 14, color: "var(--inm-texto)" }}>
    <Switch size="small" checked={checked} onChange={onChange} style={checked ? { background: "var(--inm-celeste)" } : undefined} />
    {children}
  </label>
);

export const Asistentes = ({ onEditarExperiencia, experienciaInicial }) => {
  const { handleRefreshToken } = useContext(AuthContext);
  const [experiencias, setExperiencias] = useState(null);
  const [seleccion, setSeleccion] = useState(null);
  const [form, setForm] = useState(formDesde());
  const cambiar = (campo) => (valor) => setForm((f) => ({ ...f, [campo]: valor }));
  const [guardando, setGuardando] = useState(false);
  const [estado, setEstado] = useState(null);
  //duración máxima guardada desde "Uso y costos" en esta visita, para la vista previa
  const [topeGuardado, setTopeGuardado] = useState({});
  const [error, setError] = useState(null);

  const cargar = useCallback(async () => {
    setError(null);
    try {
      const jwt = await handleRefreshToken();
      const lista = await apiService.getExperiencesByUser(jwt);
      const ordenada = (Array.isArray(lista) ? lista : []).sort(
        (a, b) =>
          Number(Boolean(b.metadata?.asistente?.habilitado)) - Number(Boolean(a.metadata?.asistente?.habilitado)) ||
          (a.name || "").localeCompare(b.name || "")
      );
      setExperiencias(ordenada);
      //si se llega desde el editor queda elegida esa experiencia; si no, la primera
      const inicial = ordenada.find((e) => e.id === experienciaInicial)?.id;
      setSeleccion((prev) => prev || inicial || ordenada[0]?.id || null);
    } catch (e) {
      setExperiencias([]);
      setError("No pudimos cargar tus experiencias.");
    }
  }, [handleRefreshToken, experienciaInicial]);

  const cargarEstado = useCallback(async () => {
    try {
      setEstado(await apiService.getEstadoAsistente());
    } catch (e) {
      //sin el estado global el bloque de uso sigue funcionando: solo no muestra el total de Inmersial
    }
  }, []);

  useEffect(() => {
    cargar();
    cargarEstado();
  }, [cargar, cargarEstado]);

  const actual = useMemo(() => (experiencias || []).find((e) => e.id === seleccion) || null, [experiencias, seleccion]);

  useEffect(() => {
    setForm(formDesde(actual?.metadata?.asistente));
  }, [actual]);

  //desde el editor se viene a acomodar el asistente: se baja directo a la vista previa, una sola vez
  const yaBajo = useRef(false);
  useEffect(() => {
    if (!actual || yaBajo.current || actual.id !== experienciaInicial) return;
    yaBajo.current = true;
    document.getElementById("asis-vista")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [actual, experienciaInicial]);

  const original = useMemo(() => actual?.metadata?.asistente || {}, [actual]);
  const modificado = JSON.stringify(form) !== JSON.stringify(formDesde(original));

  //a la vista previa solo va lo que ve el visitante: nunca los secretos de _backend
  const configVista = useMemo(() => {
    const a = armarAsistente(original, form);
    return { recorte: a.recorte, ubicacion: a.ubicacion, ui: a.ui };
  }, [original, form]);

  const guardar = async () => {
    if (!actual) return;
    setGuardando(true);
    try {
      const jwt = await handleRefreshToken();
      //metadata fresca para no pisar otros campos (recorte, ubicacion, limites, secretos del backend, enlaces, etc.)
      const fresca = await apiService.getExperienceById(actual.id);
      const metadata = fresca.metadata || {};
      const asistente = armarAsistente(metadata.asistente || {}, form);
      await apiService.updateExperienceById(jwt, actual.id, { metadata: { ...metadata, asistente } });
      setExperiencias((prev) =>
        prev.map((e) => (e.id === actual.id ? { ...e, metadata: { ...metadata, asistente } } : e))
      );
      message.success("Asistente guardado");
    } catch (e) {
      message.error("No se pudo guardar el asistente");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="inm-pagina">
      <header className="inm-pagina__cabecera">
        <div style={{ display: "flex", flexDirection: "column", gap: 6, flexGrow: 1 }}>
          <h1 className="inm-pagina__titulo">Asistentes</h1>
          <p className="inm-pagina__bajada">
            El asistente es un avatar que recibe a los visitantes, responde preguntas por voz y los guía por el recorrido.
          </p>
        </div>
      </header>

      {error && <Alert type="error" showIcon message={error} />}

      {experiencias === null ? (
        <div className="inm-caja">
          <Skeleton active />
        </div>
      ) : experiencias.length === 0 ? (
        <div className="inm-caja" style={{ alignItems: "center", padding: 40 }}>
          <Empty description="Creá una experiencia para sumarle un asistente." />
        </div>
      ) : (
        <div className="inm-fila">
          <aside className="inm-caja inm-lateral" style={{ maxWidth: 340, padding: 12 }} aria-label="Experiencias">
            <div className="inm-lista-sel">
              {experiencias.map((e) => {
                const activo = Boolean(e.metadata?.asistente?.habilitado);
                return (
                  <button
                    key={e.id}
                    type="button"
                    className={e.id === seleccion ? "is-activo" : ""}
                    aria-pressed={e.id === seleccion}
                    onClick={() => setSeleccion(e.id)}
                  >
                    <span className="inm-lista-sel__nombre">{e.name}</span>
                    <span className={`inm-pill ${activo ? "inm-pill--asistente" : "inm-pill--neutro"}`}>
                      {activo ? "Activo" : "Apagado"}
                    </span>
                  </button>
                );
              })}
            </div>
          </aside>

          {actual && (
            <div className="inm-principal" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <section className="inm-caja inm-caja--asistente" aria-labelledby="asis-titulo">
                <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
                  <div className="inm-avatar-asistente" aria-hidden="true">
                    <UserOutlined />
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6, flexGrow: 1, minWidth: 200 }}>
                    <span className="inm-tenue">Asistente de</span>
                    <h2 id="asis-titulo" className="inm-h2" style={{ fontSize: 22, color: "var(--inm-texto)" }}>
                      {actual.name}
                    </h2>
                    <label style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 14 }}>
                      <Switch
                        checked={form.habilitado}
                        onChange={(v) => setForm((f) => ({ ...f, habilitado: v }))}
                        style={form.habilitado ? { background: "var(--inm-celeste)" } : undefined}
                      />
                      {form.habilitado ? "Activo en la experiencia" : "Apagado"}
                    </label>
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <Button icon={<PlayCircleOutlined />} onClick={() => window.open(urlPublica(actual.id), "_blank", "noopener")}>
                      Probar charla
                    </Button>
                    <Button onClick={() => onEditarExperiencia(actual.id)}>Editar experiencia</Button>
                  </div>
                </div>
              </section>

              <div className="inm-fila">
                <section className="inm-caja inm-principal" aria-labelledby="asis-apariencia">
                  <h2 id="asis-apariencia" className="inm-h2">
                    <UserOutlined /> Apariencia
                  </h2>
                  <label style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 13, color: "var(--inm-texto-2)" }}>
                    ID del avatar (LiveAvatar)
                    <Input
                      value={form.avatarId}
                      placeholder="Si lo dejás vacío se usa el avatar por defecto"
                      onChange={(e) => setForm((f) => ({ ...f, avatarId: e.target.value }))}
                    />
                  </label>

                  <h2 className="inm-h2" style={{ marginTop: 8 }}>
                    <SoundOutlined /> Voz y personalidad
                  </h2>
                  <label style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 13, color: "var(--inm-texto-2)" }}>
                    ID del agente (ElevenLabs)
                    <Input
                      value={form.agenteId}
                      placeholder="Si lo dejás vacío se usa el agente por defecto"
                      onChange={(e) => setForm((f) => ({ ...f, agenteId: e.target.value }))}
                    />
                  </label>
                  <p className="inm-tenue" style={{ margin: 0 }}>
                    El saludo, la voz y las instrucciones de cómo habla se editan en el agente de ElevenLabs.
                  </p>

                  <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <Button
                      type="primary"
                      className="inm-btn-asistente"
                      loading={guardando}
                      disabled={!modificado}
                      onClick={guardar}
                    >
                      Guardar cambios
                    </Button>
                  </div>
                </section>
              </div>

              <UsoYCostos
                experienciaId={actual.id}
                estadoGlobal={estado?.global}
                //sin tocar la lista de experiencias: eso recargaría el formulario y perdería lo no guardado
                onTopeSesion={(minutos) => setTopeGuardado((t) => ({ ...t, [actual.id]: minutos }))}
              />

              <section className="inm-caja" aria-labelledby="asis-vista">
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <h2 id="asis-vista" className="inm-h2" style={{ flexGrow: 1 }}>
                    Cómo se ve en la experiencia
                  </h2>
                  <Button
                    type="primary"
                    className="inm-btn-asistente"
                    loading={guardando}
                    disabled={!modificado}
                    onClick={guardar}
                  >
                    Guardar cambios
                  </Button>
                </div>
                <div className="inm-vista-asistente">
                  <div className="inm-vista-asistente__previa">
                    <VistaPreviaAsistente
                      experiencia={actual}
                      config={configVista}
                      avatarId={form.avatarId.trim()}
                      puedeProbar={Boolean(original.habilitado)}
                      minutosPorSesion={topeGuardado[actual.id] ?? Math.max(1, Math.round((original.limites?.topeSesion || 180) / 60))}
                    />
                  </div>
                  <div className="inm-vista-asistente__controles">
                    <Campo etiqueta="Ubicación">
                      <Segmented
                        block
                        value={form.anclaje}
                        onChange={cambiar("anclaje")}
                        options={[
                          { value: "abajo-izquierda", label: "Izquierda" },
                          { value: "abajo-centro", label: "Centro" },
                          { value: "abajo-derecha", label: "Derecha" },
                        ]}
                      />
                    </Campo>
                    {form.anclaje !== "abajo-centro" && (
                      <Campo etiqueta={`Separación del costado · ${form.margenX}px`}>
                        <Slider min={0} max={200} value={form.margenX} onChange={cambiar("margenX")} />
                      </Campo>
                    )}
                    <Campo etiqueta={`Separación de abajo · ${form.margenY}px`}>
                      <Slider min={0} max={200} value={form.margenY} onChange={cambiar("margenY")} />
                    </Campo>
                    <Campo etiqueta={`Tamaño · ${form.alturaVh}% del alto de la pantalla`}>
                      <Slider min={30} max={90} value={form.alturaVh} onChange={cambiar("alturaVh")} />
                    </Campo>
                    <Campo
                      etiqueta={`Encuadre · acercar ×${form.zoom.toFixed(2)}`}
                      ayuda="Acercá para que se vea de la cintura o del pecho para arriba."
                    >
                      <Slider min={1} max={2.5} step={0.05} value={form.zoom} onChange={cambiar("zoom")} />
                    </Campo>
                    <Campo etiqueta="Encuadre · subir o bajar al avatar">
                      <Slider
                        min={-0.4}
                        max={0.4}
                        step={0.01}
                        value={form.vertical}
                        onChange={cambiar("vertical")}
                        tooltip={{ formatter: (v) => (v > 0 ? `Sube ${Math.round(v * 100)}%` : v < 0 ? `Baja ${Math.round(-v * 100)}%` : "Centrado") }}
                      />
                    </Campo>
                    <Campo etiqueta="Recorte del fondo">
                      <Segmented
                        block
                        value={form.modoRecorte}
                        onChange={cambiar("modoRecorte")}
                        options={[
                          { value: "chroma", label: "Fondo verde" },
                          { value: "ia", label: "Automático" },
                          { value: "ninguno", label: "Sin recorte" },
                        ]}
                      />
                    </Campo>
                    {form.modoRecorte === "chroma" && (
                      <Campo etiqueta={`Solidez del recorte · ${form.solidez}%`} ayuda="Más alto: bordes más definidos. Más bajo: bordes más suaves.">
                        <Slider min={0} max={100} value={form.solidez} onChange={cambiar("solidez")} />
                      </Campo>
                    )}
                    <Interruptor checked={form.subtitulos} onChange={cambiar("subtitulos")}>Mostrar subtítulos</Interruptor>
                    <Interruptor checked={form.permitirTexto} onChange={cambiar("permitirTexto")}>Permitir escribir en lugar de hablar</Interruptor>
                    <Interruptor checked={form.ocultarEntreTurnos} onChange={cambiar("ocultarEntreTurnos")}>Ocultar la guía cuando no habla</Interruptor>
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 18 }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                    <Campo etiqueta="Nombre de la guía" ayuda="Aparece en la invitación, en los subtítulos y en la conversación.">
                      <Input value={form.nombre} maxLength={40} placeholder="Por ejemplo: Lucía" onChange={(e) => cambiar("nombre")(e.target.value)} />
                    </Campo>
                    <Campo etiqueta="Texto de la invitación">
                      <Input.TextArea
                        value={form.saludo}
                        maxLength={200}
                        autoSize={{ minRows: 2, maxRows: 4 }}
                        placeholder={`¡Hola! Soy ${form.nombre || "tu guía"}. ¿Querés que te muestre la experiencia?`}
                        onChange={(e) => cambiar("saludo")(e.target.value)}
                      />
                    </Campo>
                    <Campo etiqueta="La invitación aparece">
                      <Select
                        value={form.invitacionTras}
                        onChange={cambiar("invitacionTras")}
                        options={[
                          { value: 3, label: "A los 3 segundos" },
                          { value: 6, label: "A los 6 segundos" },
                          { value: 15, label: "A los 15 segundos" },
                          { value: 0, label: "Solo al tocar el botón" },
                        ]}
                      />
                    </Campo>
                    <Campo etiqueta="Preguntas sugeridas" ayuda="Escribí una y apretá Enter. Hasta 6; se muestran como atajos en la conversación.">
                      <Select
                        mode="tags"
                        value={form.sugerencias}
                        onChange={(v) => cambiar("sugerencias")(v.slice(0, 6))}
                        open={false}
                        placeholder="¿Qué colores hay?"
                      />
                    </Campo>
                    <Campo etiqueta="Contacto al terminar" ayuda="Link que se ofrece al cerrar la charla o si no hay guías libres.">
                      <Input value={form.contacto} placeholder="https://wa.me/549..." onChange={(e) => cambiar("contacto")(e.target.value)} />
                    </Campo>
                  </div>
                </div>
              </section>

              <section className="inm-caja" aria-labelledby="asis-capacidades">
                <h2 id="asis-capacidades" className="inm-h2">
                  <StarFilled style={{ color: "var(--inm-celeste)" }} /> Qué puede hacer
                </h2>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
                  {CAPACIDADES.map((c) => (
                    <div
                      key={c.titulo}
                      style={{
                        display: "flex",
                        gap: 12,
                        padding: 14,
                        borderRadius: 12,
                        border: "1px solid var(--inm-borde)",
                        background: "var(--inm-panel)",
                      }}
                    >
                      <span style={{ color: "var(--inm-celeste)", fontSize: 20 }}>{c.icono}</span>
                      <span style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        <b style={{ fontWeight: 600 }}>{c.titulo}</b>
                        <span className="inm-tenue">{c.texto}</span>
                      </span>
                    </div>
                  ))}
                </div>
              </section>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
